import { useState } from 'react'
import { FiMail, FiCheck, FiAlertCircle, FiLoader } from 'react-icons/fi'
import { trackBlogCta } from '../hooks/useBlogTracking'

// Caja inline de captación de newsletter para insertar dentro de un post.
// - slug: del post desde el que se suscribe (para análisis de fuente).
// - variant: 'inline' (compacta) | 'card' (con marco y más respiración).
//
// Maneja doble opt-in: tras el POST muestra "Revisa tu correo" pase lo que
// pase (incluso si el email ya estaba confirmado, el endpoint responde 200
// neutro para no filtrar la pertenencia a la lista).
export default function NewsletterInline({ slug, variant = 'card' }) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState('idle') // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('')

  const onSubmit = async (e) => {
    e.preventDefault()
    if (status === 'loading') return

    const emailTrim = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      setStatus('error')
      setErrorMsg('Email no válido')
      return
    }

    setStatus('loading')
    setErrorMsg('')
    trackBlogCta(slug, 'newsletter_submit')

    try {
      const res = await fetch('/api/blog/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailTrim, fuente_post_slug: slug }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setStatus('error')
        setErrorMsg(data.error || 'No se ha podido procesar tu suscripción')
        return
      }
      setStatus('success')
      trackBlogCta(slug, 'newsletter_success')
    } catch {
      setStatus('error')
      setErrorMsg('Error de red. Inténtalo de nuevo.')
    }
  }

  if (status === 'success') {
    return (
      <div className={`my-10 ${variant === 'card' ? 'rounded-2xl border border-emerald-100 bg-emerald-50 p-6' : ''}`}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center">
            <FiCheck className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Revisa tu correo</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Te hemos enviado un email a <strong>{email}</strong> para confirmar la suscripción. Si no llega en unos minutos, revisa la carpeta de spam.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`my-10 ${variant === 'card' ? 'rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6 sm:p-8' : ''}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center">
          <FiMail className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900 leading-tight">¿Quieres más artículos como este?</h3>
          <p className="text-sm text-gray-600 mt-1">
            Recibe en tu correo nuestras guías sobre control horario, normativa laboral y cumplimiento. Sin spam, baja en un clic.
          </p>
        </div>
      </div>
      <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle') }}
          placeholder="tu@email.com"
          className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none disabled:opacity-50"
          disabled={status === 'loading'}
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors"
        >
          {status === 'loading' ? (
            <><FiLoader className="h-4 w-4 animate-spin" /> Enviando…</>
          ) : (
            <>Suscribirme</>
          )}
        </button>
      </form>
      {status === 'error' && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-red-600">
          <FiAlertCircle className="h-3.5 w-3.5" /> {errorMsg}
        </p>
      )}
      <p className="mt-3 text-[11px] text-gray-400 leading-relaxed">
        Al suscribirte aceptas que tratemos tu email para enviarte la newsletter. Puedes darte de baja en cualquier momento.
        Más información en nuestra <a href="/privacidad" className="underline hover:text-gray-600">política de privacidad</a>.
      </p>
    </div>
  )
}
